import { useState, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface CodeBlockProps {
  code: string;
  language?: string;
  filename?: string;
  showLineNumbers?: boolean;
  className?: string;
}

function highlight(code: string, language: string): ReactNode[] {
  const isJsLike = ["js", "ts", "jsx", "tsx", "javascript", "typescript"].includes(language);
  const lines = code.split("\n");

  return lines.map((line, i) => {
    if (!isJsLike) {
      return <span key={i} className="cb-line">{line}{"\n"}</span>;
    }

    const tokens: ReactNode[] = [];
    const pattern =
      /(\/\/[^\n]*)|(\/\*[\s\S]*?\*\/)|(["'`](?:\\[\s\S]|[^"'`\\])*["'`])|(\b(?:import|export|from|const|let|var|function|return|if|else|for|while|class|extends|new|typeof|instanceof|async|await|default|type|interface|enum|as|of|in|switch|case|break|continue|throw|try|catch|finally)\b)|(\b(?:true|false|null|undefined|void|never|any|string|number|boolean)\b)|(\b\d+(?:\.\d+)?\b)|([a-zA-Z_$][\w$]*(?=\s*\())|(<\/?[A-Z][a-zA-Z]*)/g;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) tokens.push(line.slice(lastIndex, match.index));
      const [text, comment1, comment2, string_, keyword, literal, number_, fn, jsx] = match;
      if (comment1 || comment2)
        tokens.push(<span key={tokens.length} className="cb-comment">{text}</span>);
      else if (string_)
        tokens.push(<span key={tokens.length} className="cb-string">{text}</span>);
      else if (keyword)
        tokens.push(<span key={tokens.length} className="cb-keyword">{text}</span>);
      else if (literal)
        tokens.push(<span key={tokens.length} className="cb-literal">{text}</span>);
      else if (number_)
        tokens.push(<span key={tokens.length} className="cb-number">{text}</span>);
      else if (fn)
        tokens.push(<span key={tokens.length} className="cb-function">{text}</span>);
      else if (jsx)
        tokens.push(<span key={tokens.length} className="cb-jsx">{text}</span>);
      else
        tokens.push(text);
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < line.length) tokens.push(line.slice(lastIndex));
    return <span key={i} className="cb-line">{tokens}{"\n"}</span>;
  });
}

export function CodeBlock({
  code,
  language = "tsx",
  filename,
  showLineNumbers = false,
  className,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = highlight(code, language);

  return (
    <div className={cn("code-block", className)}>
      <div className="code-block-header">
        <div className="code-block-header-left">
          {filename && <span className="code-block-filename">{filename}</span>}
          <span className="code-block-lang">{language}</span>
        </div>
        <button className="code-block-copy" onClick={copy} aria-label={copied ? "Copied" : "Copy code"}>
          {copied ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
          )}
          <span>{copied ? "Copied!" : "Copy"}</span>
        </button>
      </div>
      <div className="code-block-body">
        {showLineNumbers && (
          <div className="code-block-line-numbers" aria-hidden="true">
            {code.split("\n").map((_, i) => (
              <span key={i}>{i + 1}</span>
            ))}
          </div>
        )}
        <pre className="code-block-pre"><code className="code-block-code">{lines}</code></pre>
      </div>
    </div>
  );
}
